import React, { useState, ChangeEvent } from 'react'
import { Field, useFormikContext } from 'formik'
import { TextInputField, CheckboxField } from '@toasttab/buffet-pui-forms'
import { FulfillmentSelectorModal } from '../../FulfillmentSelectorModal/FulfillmentSelectorModal'
import { IconButton } from '@toasttab/buffet-pui-buttons'
import { EditIcon } from '@toasttab/buffet-pui-icons'
import { ReadonlyText } from '@toasttab/buffet-pui-readonly'
import { DeliveryInfo } from '../../../types/orders'
import { useFulfillment } from '../../FulfillmentProvider/FulfillmentProvider'
import { useCustomerAuth } from '../../../auth/CustomerAuthContext'
import {
  addressToString,
  customerAddressToDeliveryInfo
} from '../../../utils/address-helpers'

interface FormValues {
  saveNewAddress: boolean
  addressLabel?: string
  customerDeliveryInfoNotes?: string
  customerDeliveryInfoAddress?: DeliveryInfo
}

export const NewAddress = () => {
  const { customer } = useCustomerAuth()
  const customerAddresses = customer?.addresses || []

  const { values, setFieldTouched } = useFormikContext<FormValues>()

  const deliveryAddressStr = addressToString(values.customerDeliveryInfoAddress)

  const isSavedAddress =
    !!deliveryAddressStr &&
    !!customerAddresses.find((customerAddress) => {
      const address = customerAddressToDeliveryInfo(customerAddress)
      return addressToString(address) === deliveryAddressStr
    })

  // This ensures the required warning shows up when the checkbox is checked
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setFieldTouched('addressLabel', true)
    }
  }

  return (
    <>
      <Field
        id='customer_delivery_info_address'
        name='customerDeliveryInfoAddress'
        component={ReadOnlyAddressField}
      />
      <TextInputField
        containerClassName='mb-2'
        data-testid='customer_delivery_info_notes'
        id='customer_delivery_info_notes'
        label='Delivery instructions'
        name='customerDeliveryInfoNotes'
        type='text'
      />
      {!isSavedAddress && (
        <>
          <CheckboxField
            data-testid='save_address_checkbox'
            id='save_address_checkbox'
            label={
              customer
                ? 'Save address'
                : 'Sign in to save address for next time'
            }
            name='saveNewAddress'
            onChange={handleCheckboxChange}
            disabled={!customer}
          />
          {values.saveNewAddress && (
            <Field name='addressLabel' validateOnChange>
              {({ field, meta }) => (
                <TextInputField
                  {...field}
                  containerClassName='mb-2'
                  data-testid='address_label'
                  id='address_label'
                  label='Address label'
                  type='text'
                  placeholder='e.g. Home, Work, etc.'
                  required
                  error={meta.error}
                />
              )}
            </Field>
          )}
        </>
      )}
    </>
  )
}

const ReadOnlyAddressField = ({ field }) => {
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false)
  return (
    <>
      <ReadonlyText
        testId='ReadOnlyAddressField'
        containerClassName='mb-3 text-color-default'
        label={
          <div className='flex items-center -mb-1'>
            <span className='text-error mr-0.5'>*</span>Address
            <IconButton
              className='ml-1'
              icon={<EditIcon size='xs' />}
              onClick={() => setShowFulfillmentModal(true)}
            />
          </div>
        }
        value={<FormattedAddressTwoLines address={field.value} />}
      />
      {showFulfillmentModal && (
        <FulfillmentSelectorModal
          onClose={() => setShowFulfillmentModal(false)}
        />
      )}
    </>
  )
}

const FormattedAddressTwoLines = ({ address }: { address: DeliveryInfo }) => {
  const { customLocationName } = useFulfillment()

  if (!address) return <></>
  const { address1, address2, city, state, zipCode } = address
  if (!(address1 && city && state && zipCode)) return <></>

  return (
    <>
      {customLocationName && (
        <>
          {customLocationName}
          <br />
        </>
      )}
      {address1} {address2}
      <br />
      {city}, {state} {zipCode}
    </>
  )
}
